import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchUsers, fetchPermissionRegistry, createStaffUser } from "../pages/services/userManagementService";
import "../styles/ListPage.css";
import "../styles/AddForm.css";

// User Management — every user of the platform in one list, whatever kind they
// are, with what each of them is allowed to do.
//
// Students, trainers, admins and staff live in four separate collections with
// four separate logins. They are listed together here but never merged: the
// pair (userType, id) is what identifies a person.

const ROLE_COLOURS = {
  student:          { bg: "#eff6ff", fg: "#1d4ed8" },
  trainer:          { bg: "#ecfdf5", fg: "#047857" },
  admin:            { bg: "#fef2f2", fg: "#b91c1c" },
  content_engineer: { bg: "#f5f3ff", fg: "#6d28d9" },
  sales_executive:  { bg: "#fff7ed", fg: "#c2410c" },
};

const Pill = ({ role, label }) => {
  const c = ROLE_COLOURS[role] || { bg: "#f1f5f9", fg: "#475569" };
  return <span style={{ background: c.bg, color: c.fg, padding: "3px 10px", borderRadius: 10, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>{label}</span>;
};

const UserManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [registry, setRegistry] = useState({ roles: [], permissions: {} });
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");

  const [showAdd, setShowAdd] = useState(false);
  const [staffForm, setStaffForm] = useState({ name: "", contactNumber: "", emailId: "", role: "content_engineer" });
  const [staffErrors, setStaffErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (location.state?.successMessage) {
      setToast(location.state.successMessage);
      window.history.replaceState({}, "");
      setTimeout(() => setToast(""), 3000);
    }
  }, [location.state]);

  useEffect(() => { fetchPermissionRegistry().then(setRegistry).catch((e) => setError(e.message)); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const json = await fetchUsers({ role, search, status });
      setUsers(json.data || []);
      setCounts(json.counts || {});
      setTotal(json.total || 0);
      setError("");
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [role, search, status]);

  // Debounced so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const staffRoles = registry.roles.filter((r) => r.staff);

  const submitStaff = async (e) => {
    e.preventDefault();
    setSaving(true); setStaffErrors({});
    try {
      await createStaffUser(staffForm);
      setShowAdd(false);
      setStaffForm({ name: "", contactNumber: "", emailId: "", role: "content_engineer" });
      setToast("Staff user created");
      setTimeout(() => setToast(""), 3000);
      load();
    } catch (err) {
      if (err.fields) setStaffErrors(err.fields);
      setError(err.message);
    } finally { setSaving(false); }
  };

  const permissionCount = (r) => (registry.permissions?.[r] || []).length;

  return (
    <div className="lp-page">
      {toast && <div className="lp-toast">{toast}</div>}

      <div className="lp-header">
        <div className="lp-header-left">
          <h2 className="lp-title">User Management</h2>
          <span className="lp-count">{total} users</span>
        </div>
        <div className="lp-header-actions">
          <button className="lp-btn-primary" onClick={() => setShowAdd(true)}>+ Add Staff User</button>
        </div>
      </div>

      {error && <div className="af-alert af-alert-error" style={{ margin: "0 0 14px" }}>{error}</div>}

      {/* Role tabs — each shows how many users it holds. */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        <button onClick={() => setRole("")} className={role === "" ? "lp-btn-primary" : "lp-btn-secondary"}>
          All ({Object.values(counts).reduce((a, b) => a + b, 0)})
        </button>
        {registry.roles.map((r) => (
          <button key={r.key} onClick={() => setRole(r.key)} className={role === r.key ? "lp-btn-primary" : "lp-btn-secondary"}>
            {r.label} ({counts[r.key] ?? 0})
          </button>
        ))}
      </div>

      <div className="lp-toolbar">
        <div className="lp-search-wrap">
          <span className="lp-search-icon">&#128269;</span>
          <input type="text" className="lp-search" placeholder="Search name, phone or email…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="lp-search" style={{ maxWidth: 190 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
      </div>

      <div className="lp-table-wrap">
        <table className="lp-table">
          <thead>
            <tr>
              <th>Name</th><th>Role</th><th>Contact</th><th>Email</th><th>Status</th><th>Permissions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>No users match these filters.</td></tr>
            ) : users.map((u) => (
              <tr key={`${u.userType}:${u._id}`} style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/users/${u.userType}/${u._id}`)}>
                <td style={{ fontWeight: 600 }}>{u.name || "—"}</td>
                <td><Pill role={u.userType} label={u.roleLabel} /></td>
                <td>{u.contactNumber || "—"}</td>
                <td>{u.emailId || "—"}</td>
                <td>
                  <span style={{ background: u.isActive ? "#dcfce7" : "#f1f5f9", color: u.isActive ? "#166534" : "#64748b",
                                 padding: "2px 9px", borderRadius: 999, fontSize: 11.5, fontWeight: 700 }}>
                    {u.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  {u.userType === "admin" ? (
                    <span style={{ color: "#64748b", fontSize: 12.5 }}>Unrestricted</span>
                  ) : u.customPermissionCount > 0 ? (
                    <span style={{ color: "#b45309", fontWeight: 700, fontSize: 12.5 }}>
                      {u.customPermissionCount} changed from default
                    </span>
                  ) : (
                    <span style={{ color: "#94a3b8", fontSize: 12.5 }}>Default ({permissionCount(u.userType)})</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add staff — only Content Engineers and Sales Executives are created
          here. Students, trainers and admins have their own screens. */}
      {showAdd && (
        <div className="af-overlay" style={{ alignItems: "flex-start", paddingTop: "8vh" }}>
          <div className="af-card" style={{ maxWidth: 520, width: "92%", margin: 0 }} onClick={(e) => e.stopPropagation()}>
            <div className="af-header">
              <div className="af-header-icon indigo">👤</div>
              <div className="af-header-text" style={{ flex: 1 }}>
                <h2 className="af-title">Add Staff User</h2>
                <p className="af-subtitle">Content Engineers and Sales Executives</p>
              </div>
            </div>
            <form onSubmit={submitStaff}>
              <div className="af-body">
                <div className="af-field">
                  <label className="af-label">Role<span className="af-required">*</span></label>
                  <select value={staffForm.role} onChange={(e) => setStaffForm((p) => ({ ...p, role: e.target.value }))}
                          className={staffErrors.role ? "error-input" : ""}>
                    {staffRoles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                  </select>
                  {staffErrors.role && <span className="af-error">{staffErrors.role}</span>}
                  <span className="af-hint" style={{ display: "block", marginTop: 6, color: "#6b7280", fontSize: 12 }}>
                    Starts with this role's default permissions — {permissionCount(staffForm.role)} in total. Adjust them after creating.
                  </span>
                </div>
                <div className="af-field">
                  <label className="af-label">Full Name<span className="af-required">*</span></label>
                  <input type="text" placeholder="e.g. Priya Sharma" value={staffForm.name}
                    onChange={(e) => setStaffForm((p) => ({ ...p, name: e.target.value }))}
                    className={staffErrors.name ? "error-input" : ""} />
                  {staffErrors.name && <span className="af-error">{staffErrors.name}</span>}
                </div>
                <div className="af-field">
                  <label className="af-label">Contact Number<span className="af-required">*</span></label>
                  <input type="text" placeholder="10-digit mobile number" value={staffForm.contactNumber}
                    onChange={(e) => setStaffForm((p) => ({ ...p, contactNumber: e.target.value }))}
                    className={staffErrors.contactNumber ? "error-input" : ""} />
                  {staffErrors.contactNumber && <span className="af-error">{staffErrors.contactNumber}</span>}
                </div>
                <div className="af-field">
                  <label className="af-label">Email</label>
                  <input type="email" placeholder="name@myskillverse.ai" value={staffForm.emailId}
                    onChange={(e) => setStaffForm((p) => ({ ...p, emailId: e.target.value }))} />
                </div>
                <p style={{ fontSize: 12, color: "#6b7280", margin: "4px 0 0" }}>
                  Their panel and sign-in are not built yet — this creates the account and its permissions, ready for when they are.
                </p>
              </div>
              <div className="af-submit-row" style={{ display: "flex", gap: 12 }}>
                <button type="button" className="af-secondary-btn" onClick={() => { setShowAdd(false); setStaffErrors({}); }}>Cancel</button>
                <button type="submit" className="af-submit" disabled={saving} style={{ flex: 1 }}>
                  {saving ? "Creating…" : "Create Staff User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
